
import { defineComponent, ref, getCurrentInstance, onMounted, onUnmounted } from 'vue';
import Toast from 'vant/lib/toast';
import CellGroup from 'vant/lib/cell-group';
import Cell from 'vant/lib/cell';
// eslint-disable-next-line import/no-extraneous-dependencies
import debounce from 'lodash/debounce';
import LoginPopup from '@/components/login/index.vue';
import DeleteAccountPopup, { PopupContentType } from '@/components/logout-popup/index.vue';
import { UserApi } from '@/api/user';

enum LogoutType {
    DeleteAccout = 1,
    Logout = 2,
}

interface ActionType {
    key?: string | number;
    desc?: string;
    onClick?: (type: LogoutType) => Promise<void> | void;
}

interface ResponseBase {
    result?: number;
    error_msg?: string;
}

const LogoutApiMap: Record<LogoutType, string> = {
    [LogoutType.Logout]: '/rest/o/w/account/logout',
    [LogoutType.DeleteAccout]: '/rest/o/w/account/delete/do',
};

export default defineComponent({
    components: {
        LoginPopup,
        DeleteAccountPopup,
        VantCell: Cell,
        VantCellGroup: CellGroup,
    },
    layout(ctx) {
        return ctx.$pwaLayout || 'default';
    },
    setup() {
        const loginPopupVisible = ref(false);
        const deleteAccountPopupVisible = ref(false);
        const isLoginStatus = ref(false);
        const { $axios, $root, $store } = getCurrentInstance()?.proxy || {};

        const checkLogin = async () => {
            const res = await UserApi.getUserInfo({});

            return res?.result === 1 && !!res?.user?.user_id;
        };

        const handleChangeLoginPopupVisible = (visible: boolean) => {
            loginPopupVisible.value = visible;
        };

        const handleLoginSuccessfully = (isLogin?: boolean) => {
            if (isLogin) {
                handleChangeLoginPopupVisible(false);
                isLoginStatus.value = true;
            }
        };

        const handleChangeDeleteAccountPopupVisible = (visible: boolean) => {
            deleteAccountPopupVisible.value = visible;
        };

        const handleLogoutSuccessfully = () => {
            isLoginStatus.value = false;
            handleChangeDeleteAccountPopupVisible(false);
        };

        const handleLogout = async () => {
            const res = await $axios?.$$get?.<ResponseBase>(LogoutApiMap[LogoutType.Logout], {});
            if (res?.result !== 1) {
                res?.error_msg &&
                    Toast({
                        message: res.error_msg,
                        duration: 2500,
                    });
                return;
            }
            Toast({
                message: $root?.$t('k_1218965') as string,
                duration: 2500,
            });
            $store?.commit?.('setIsLogin', false);
            handleLogoutSuccessfully();
        };

        const handleAction = debounce(async (type: LogoutType) => {
            if (!isLoginStatus.value) {
                handleChangeLoginPopupVisible(true);
                return;
            }

            if (type === LogoutType.DeleteAccout) {
                handleChangeDeleteAccountPopupVisible(true);
                return;
            }

            await handleLogout();
        }, 300);

        const actionList: ActionType[] = [
            {
                key: LogoutType.DeleteAccout,
                desc: $root?.$t?.('k_1218479') as string,
                onClick: () => handleAction(LogoutType.DeleteAccout),
            },
            {
                key: LogoutType.Logout,
                desc: $root?.$t?.('k_1218480') as string,
                onClick: () => handleAction(LogoutType.Logout),
            },
        ];

        onMounted(async () => {
            const isLogin = await checkLogin();
            isLoginStatus.value = isLogin;
        });

        return {
            actionList,
            loginPopupVisible,
            PopupContentType,
            deleteAccountPopupVisible,
            isTopick: window?.location?.host?.includes?.('topick-app'),
            handleChangeLoginPopupVisible,
            handleChangeDeleteAccountPopupVisible,
            handleLoginSuccessfully,
            handleLogoutSuccessfully,
        };
    },

    asyncData() {
        return {};
    },
});
